import { useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import * as Dialog from '@radix-ui/react-dialog';
import type { PackAsset } from '../lib/assets';
import type { PackKind, PackState } from '../lib/packs';
import { formatBytes } from '../lib/uploadFile';

/**
 * Sortable thumbnail grid for the pack editor.
 *
 * Features:
 *   - Drag-drop reorder via `@dnd-kit/sortable`. Local "preview order"
 *     persists until the admin clicks "Lưu thứ tự"; only then do we
 *     PATCH the Worker.
 *   - Per-tile delete with state guard: enabled only when
 *     `packState === 'draft'`. On non-draft packs the button is
 *     disabled with a tooltip directing the user to unpublish first.
 *     Confirmation goes through a Radix dialog (Requirement 22.4).
 *   - Click → full-resolution preview modal. Sticker tiles render as
 *     `<img>`; GIF tiles render their bytes directly so they loop.
 *   - Per-tile tooltip with file name, byte size, and dimensions.
 *
 * Asset URL note: the public route `/v1/assets/{packId}/{assetId}` only
 * serves PUBLISHED packs (Requirement 13.3 / 15.5). For draft packs the
 * Worker has no admin-authenticated preview endpoint yet, so previews
 * fall back to a placeholder. This is captured in the design as a
 * known limitation pending a future Worker route.
 *
 * Validates Requirements 22.3, 22.4, 24.1, 24.2.
 */

export interface AssetGridProps {
  packId: string;
  packKind: PackKind;
  packState: PackState;
  /** Server-provided list, ordered by `position ASC`. */
  assets: PackAsset[];
  /** Asset currently set as the pack cover, if any. */
  coverAssetId: string | null;
  /** Whether the editor is currently saving the order. */
  isSavingOrder: boolean;
  /** Whether a delete is currently in flight. */
  isDeleting: boolean;
  /** Whether a cover update is in flight. */
  isSettingCover: boolean;
  /** Persist a new order to the Worker; resolves on success. */
  onSaveOrder: (order: Array<{ assetId: string; position: number }>) => Promise<void>;
  /** Delete a single asset; resolves on success. */
  onDeleteAsset: (assetId: string) => Promise<void>;
  /** Mark this asset as the pack cover; resolves on success. */
  onSetCover: (assetId: string) => Promise<void>;
}

export default function AssetGrid({
  packId,
  packKind,
  packState,
  assets,
  coverAssetId,
  isSavingOrder,
  isDeleting,
  isSettingCover,
  onSaveOrder,
  onDeleteAsset,
  onSetCover,
}: AssetGridProps): JSX.Element {
  // Local preview order; resets whenever the server list changes (e.g.
  // after a successful save or upload refetch).
  const [order, setOrder] = useState<PackAsset[]>(assets);
  useEffect(() => setOrder(assets), [assets]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const dirty = useMemo(() => {
    if (order.length !== assets.length) return true;
    for (let i = 0; i < order.length; i++) {
      if (order[i]!.assetId !== assets[i]!.assetId) return true;
    }
    return false;
  }, [order, assets]);

  const onDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.findIndex((a) => a.assetId === active.id);
    const newIndex = order.findIndex((a) => a.assetId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setOrder(arrayMove(order, oldIndex, newIndex));
  };

  const isDraft = packState === 'draft';

  const [previewAsset, setPreviewAsset] = useState<PackAsset | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<PackAsset | null>(
    null,
  );

  const handleSaveOrder = async (): Promise<void> => {
    const payload = order.map((a, idx) => ({
      assetId: a.assetId,
      position: idx,
    }));
    await onSaveOrder(payload);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (confirmingDelete === null) return;
    await onDeleteAsset(confirmingDelete.assetId);
    setConfirmingDelete(null);
  };

  return (
    <section aria-labelledby="asset-grid-heading" className="space-y-3">
      <header className="flex items-center justify-between">
        <h3
          id="asset-grid-heading"
          className="text-sm font-semibold text-slate-900"
        >
          Asset ({assets.length})
        </h3>
        <button
          type="button"
          onClick={() => {
            void handleSaveOrder();
          }}
          disabled={!dirty || isSavingOrder || !isDraft}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          title={
            !isDraft
              ? 'Chuyển pack về trạng thái nháp trước khi sắp xếp lại'
              : undefined
          }
        >
          {isSavingOrder ? 'Đang lưu…' : 'Lưu thứ tự'}
        </button>
      </header>

      {assets.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
          Chưa có asset nào. Tải lên ở khung phía trên.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={order.map((a) => a.assetId)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3">
              {order.map((asset) => (
                <AssetTile
                  key={asset.assetId}
                  asset={asset}
                  packId={packId}
                  packKind={packKind}
                  packState={packState}
                  isCover={asset.assetId === coverAssetId}
                  onPreview={() => setPreviewAsset(asset)}
                  onRequestDelete={() => setConfirmingDelete(asset)}
                  onSetCover={() => {
                    void onSetCover(asset.assetId);
                  }}
                  deleteDisabled={!isDraft || isDeleting}
                  setCoverDisabled={isSettingCover}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <PreviewDialog
        asset={previewAsset}
        packId={packId}
        packKind={packKind}
        packState={packState}
        onClose={() => setPreviewAsset(null)}
      />
      <DeleteAssetDialog
        asset={confirmingDelete}
        isDeleting={isDeleting}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        onCancel={() => setConfirmingDelete(null)}
      />
    </section>
  );
}

interface AssetTileProps {
  asset: PackAsset;
  packId: string;
  packKind: PackKind;
  packState: PackState;
  isCover: boolean;
  onPreview: () => void;
  onRequestDelete: () => void;
  onSetCover: () => void;
  deleteDisabled: boolean;
  setCoverDisabled: boolean;
}

function AssetTile({
  asset,
  packId,
  packKind,
  packState,
  isCover,
  onPreview,
  onRequestDelete,
  onSetCover,
  deleteDisabled,
  setCoverDisabled,
}: AssetTileProps): JSX.Element {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: asset.assetId });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0) scaleX(${transform.scaleX ?? 1}) scaleY(${transform.scaleY ?? 1})`
      : undefined,
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const url = assetPreviewUrl(packId, asset, packState);
  const tooltip = `${asset.fileName}\n${formatBytes(asset.byteSize)}${
    asset.width && asset.height ? `\n${asset.width}×${asset.height}` : ''
  }`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative flex h-[120px] w-[120px] flex-col items-stretch overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <button
        type="button"
        onClick={onPreview}
        title={tooltip}
        aria-label={`Xem trước ${asset.fileName}`}
        className="relative flex-1 cursor-zoom-in overflow-hidden bg-slate-50"
      >
        {url ? (
          packKind === 'gif' ? (
            // GIF tiles render as <img>; the browser loops the GIF natively.
            <img
              src={url}
              alt={asset.fileName}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              src={url}
              alt={asset.fileName}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-100 text-xs text-slate-500">
            Bản nháp — chưa có URL
          </div>
        )}
        {isCover ? (
          <span className="absolute left-1 top-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white shadow">
            Bìa
          </span>
        ) : null}
      </button>
      <div className="flex items-center justify-between gap-1 border-t border-slate-100 bg-white/95 px-1.5 py-1 text-[10px] text-slate-500">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Kéo để sắp xếp lại ${asset.fileName}`}
          className="cursor-grab rounded px-1 py-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
        >
          ⋮⋮
        </button>
        <button
          type="button"
          onClick={onSetCover}
          disabled={setCoverDisabled || isCover}
          aria-label={isCover ? `Đang là bìa của pack` : `Đặt ${asset.fileName} làm bìa`}
          title={isCover ? 'Đang là bìa' : 'Đặt làm bìa'}
          className="rounded p-1 text-slate-500 hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:text-amber-500"
        >
          {isCover ? '★' : '☆'}
        </button>
        <button
          type="button"
          onClick={onRequestDelete}
          disabled={deleteDisabled}
          aria-label={`Xoá ${asset.fileName}`}
          title={
            deleteDisabled
              ? 'Chuyển pack về trạng thái nháp trước khi xoá asset'
              : undefined
          }
          className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent disabled:hover:text-slate-300"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

/**
 * Compute a preview URL for an asset. Public route only works for
 * PUBLISHED packs; we return null for draft / unpublished and let the
 * tile render the placeholder.
 *
 * Uses the absolute Worker origin because the Pages domain
 * (`skey-admin.pages.dev`) doesn't proxy `/v1/*`. The Worker exposes
 * `/v1/*` with `Access-Control-Allow-Origin: *` so cross-origin
 * `<img>` loads work without auth.
 */
function assetPreviewUrl(
  packId: string,
  asset: PackAsset,
  packState: PackState,
): string | null {
  if (packState !== 'published') return null;
  return `https://skey-worker.sonhangtravel.workers.dev/v1/assets/${encodeURIComponent(packId)}/${encodeURIComponent(asset.assetId)}`;
}

interface PreviewDialogProps {
  asset: PackAsset | null;
  packId: string;
  packKind: PackKind;
  packState: PackState;
  onClose: () => void;
}

function PreviewDialog({
  asset,
  packId,
  packKind,
  packState,
  onClose,
}: PreviewDialogProps): JSX.Element {
  const url = asset ? assetPreviewUrl(packId, asset, packState) : null;
  return (
    <Dialog.Root
      open={asset !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/60" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-lg border border-slate-200 bg-white p-4 shadow-xl focus:outline-none">
          {asset ? (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Dialog.Title className="truncate text-base font-semibold text-slate-900">
                    {asset.fileName}
                  </Dialog.Title>
                  <Dialog.Description className="mt-0.5 text-xs text-slate-500">
                    {formatBytes(asset.byteSize)}
                    {asset.width && asset.height
                      ? ` · ${asset.width}×${asset.height}`
                      : ''}{' '}
                    · sha256{' '}
                    <code className="select-all font-mono">
                      {asset.sha256}
                    </code>
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Đóng"
                    className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    ✕
                  </button>
                </Dialog.Close>
              </div>
              <div className="mt-3 flex items-center justify-center rounded-md bg-slate-50 p-4">
                {url ? (
                  packKind === 'gif' ? (
                    <img
                      src={url}
                      alt={asset.fileName}
                      className="max-h-[70vh] max-w-full"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={asset.fileName}
                      className="max-h-[70vh] max-w-full"
                    />
                  )
                ) : (
                  <p className="px-6 py-8 text-center text-sm text-slate-500">
                    Asset thuộc pack chưa phát hành — bản xem trước sẽ sẵn
                    dùng sau khi pack được phát hành.
                  </p>
                )}
              </div>
            </>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface DeleteAssetDialogProps {
  asset: PackAsset | null;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteAssetDialog({
  asset,
  isDeleting,
  onConfirm,
  onCancel,
}: DeleteAssetDialogProps): JSX.Element {
  return (
    <Dialog.Root
      open={asset !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-lg focus:outline-none">
          <Dialog.Title className="text-lg font-semibold text-slate-900">
            Xoá asset?
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-slate-500">
            {asset
              ? `Xác nhận xoá tệp ${asset.fileName}. Hành động này không thể hoàn tác.`
              : null}
          </Dialog.Description>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isDeleting}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Huỷ
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-red-400"
            >
              {isDeleting ? 'Đang xoá…' : 'Xoá'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
